/// <reference types="@types/google.maps" />

import { Injectable } from '@angular/core';
import { IUserLocation, IDistanceResult, IGeometricShape } from '@core/interfaces';
import { DeliveryZone, Store as StoreModel } from '@core/models';
import { Store } from '@ngrx/store';
import { NOTIFICATION_SHOW } from '@store/actions';
import { Observable, from, map, mergeMap, of, Subject } from 'rxjs';

declare global {
  interface Window {
    google: typeof google;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private geocoder: google.maps.Geocoder;
  private _autocompletePlaceSelected = new Subject<google.maps.places.PlaceResult | null>();
  public autocompletePlaceSelected$ = this._autocompletePlaceSelected.asObservable();


  constructor(
    private store: Store<StoreModel>
  ) {
    this.geocoder = new google.maps.Geocoder();
  }

  // Verifica el estado del permiso de geolocalización
  checkGeolocationPermission(): Promise<PermissionState> {
    return new Promise((resolve) => {
      if (!navigator.permissions) {
        // Fallback para navegadores que no soportan Permissions API
        resolve('prompt');
        return;
      }

      navigator.permissions.query({ name: 'geolocation' as PermissionName })
        .then((permissionStatus) => {
          resolve(permissionStatus.state);
        })
        .catch(() => {
          // Fallback si hay error al consultar permisos
          resolve('prompt');
        });
    });
  }

  /**
   * Indica si el usuario ya concedió el permiso de geolocalización.
   * No solicita el permiso; para eso debe usarse requestGeolocationPermission().
   */
  hasGeolocationPermission(): Observable<boolean> {
    if (!navigator.geolocation) {
      return of(false);
    }

    return from(this.checkGeolocationPermission()).pipe(
      map(permissionState => permissionState === 'granted')
    );
  }

  /**
   * Solicita permisos de geolocalización explícitamente
   */
  requestGeolocationPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(false);
        return;
      }

      // Usar getCurrentPosition con timeout muy corto para solo solicitar permiso
      const timeoutId = setTimeout(() => {
        resolve(false);
      }, 100);

      navigator.geolocation.getCurrentPosition(
        () => {
          clearTimeout(timeoutId);
          resolve(true);
        },
        (error) => {
          clearTimeout(timeoutId);
          resolve(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 100,
          maximumAge: Infinity
        }
      );
    });
  }

  /**
   * Fuerza la solicitud de permisos de geolocalización
   * Útil cuando el usuario ha denegado permisos anteriormente
   */
  forceRequestGeolocationPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(false);
        return;
      }

      // Estrategia 1: Intentar con diferentes opciones para forzar el prompt
      const tryGetLocation = (options: PositionOptions, attempt: number = 1) => {
        const timeoutId = setTimeout(() => {
          if (attempt < 3) {
            // Reintentar con diferentes opciones
            const newOptions = {
              ...options,
              timeout: (options.timeout || 3000) + 2000,
              enableHighAccuracy: !options.enableHighAccuracy
            };
            tryGetLocation(newOptions, attempt + 1);
          } else {
            resolve(false);
          }
        }, options.timeout || 3000);

        navigator.geolocation.getCurrentPosition(
          () => {
            clearTimeout(timeoutId);
            resolve(true);
          },
          (error) => {
            clearTimeout(timeoutId);

            if (error.code === error.PERMISSION_DENIED) {
              if (attempt < 3) {
                // Intentar con watchPosition en lugar de getCurrentPosition
                this.tryWatchPosition(resolve);
              } else {
                this.store.dispatch(
                  NOTIFICATION_SHOW({
                    notification: {
                      type: 'error',
                      title: 'Permiso Denegado',
                      detail: 'Para usar esta función, necesitas habilitar la ubicación en la configuración de tu navegador. Ve a Configuración > Privacidad y seguridad > Ubicación y habilita el acceso.',
                    },
                  })
                );
                resolve(false);
              }
            } else {
              resolve(false);
            }
          },
          options
        );
      };

      // Comenzar con opciones básicas
      tryGetLocation({
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 0
      });
    });
  }

  /**
   * Intenta usar watchPosition para forzar el prompt de permisos
   */
  private tryWatchPosition(resolve: (value: boolean) => void): void {
    const timeoutId = setTimeout(() => {
      resolve(false);
    }, 3000);

    const watchId = navigator.geolocation.watchPosition(
      () => {
        clearTimeout(timeoutId);
        navigator.geolocation.clearWatch(watchId);
        resolve(true);
      },
      (error) => {
        clearTimeout(timeoutId);
        navigator.geolocation.clearWatch(watchId);
        resolve(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 3000,
        maximumAge: 0
      }
    );
  }

  /**
   * Método alternativo que intenta múltiples estrategias para forzar el prompt
   */
  forceNativePermissionPrompt(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(false);
        return;
      }

      // Estrategia 1: Intentar con getCurrentPosition
      const tryStrategy1 = () => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(true),
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              // Estrategia 2: Intentar con watchPosition
              tryStrategy2();
            } else {
              resolve(false);
            }
          },
          { enableHighAccuracy: false, timeout: 2000, maximumAge: 0 }
        );
      };

      // Estrategia 2: Usar watchPosition
      const tryStrategy2 = () => {
        const watchId = navigator.geolocation.watchPosition(
          () => {
            navigator.geolocation.clearWatch(watchId);
            resolve(true);
          },
          (error) => {
            navigator.geolocation.clearWatch(watchId);
            if (error.code === error.PERMISSION_DENIED) {
              // Estrategia 3: Intentar con diferentes opciones
              tryStrategy3();
            } else {
              resolve(false);
            }
          },
          { enableHighAccuracy: true, timeout: 2000, maximumAge: 0 }
        );
      };

      // Estrategia 3: Intentar con opciones diferentes
      const tryStrategy3 = () => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(true),
          () => {
            // Si todas las estrategias fallan, mostrar instrucciones manuales
            this.store.dispatch(
              NOTIFICATION_SHOW({
                notification: {
                  type: 'error',
                  title: 'Permiso Requerido',
                  detail: 'El navegador no permite solicitar permisos de ubicación automáticamente. Por favor, haz clic en el ícono de ubicación en la barra de direcciones y selecciona "Permitir".',
                },
              })
            );
            resolve(false);
          },
          { enableHighAccuracy: false, timeout: 1000, maximumAge: Infinity }
        );
      };

      // Comenzar con la primera estrategia
      tryStrategy1();
    });
  }

  supportsPermissionsAPI(): boolean {
    return 'permissions' in navigator;
  }

  getGeolocationPermissionInfo(): Promise<{
    supported: boolean;
    permissionState: PermissionState;
    canRequest: boolean;
    message: string;
  }> {
    return new Promise(async (resolve) => {
      const supported = !!navigator.geolocation;

      if (!supported) {
        resolve({
          supported: false,
          permissionState: 'denied' as PermissionState,
          canRequest: false,
          message: 'La geolocalización no está disponible en este navegador'
        });
        return;
      }

      const permissionState = await this.checkGeolocationPermission();
      const canRequest = permissionState === 'prompt' || permissionState === 'granted';

      let message = '';
      switch (permissionState) {
        case 'granted':
          message = 'Permiso de ubicación concedido';
          break;
        case 'denied':
          message = 'Permiso de ubicación denegado. Debes habilitarlo manualmente en la configuración del navegador.';
          break;
        case 'prompt':
          message = 'Permiso de ubicación pendiente. Se solicitará cuando sea necesario.';
          break;
      }

      resolve({
        supported: true,
        permissionState,
        canRequest,
        message
      });
    });
  }

  /**
   * Obtiene la ubicación del usuario con manejo mejorado de permisos
   */
  getUserLocation(): Observable<IUserLocation> {
    return from(
      new Promise<IUserLocation>(async (resolve, reject) => {
        if (!navigator.geolocation) {
          reject('La geolocalización no está disponible en este navegador');
          return;
        }

        // Verificar el estado del permiso
        const permissionState = await this.checkGeolocationPermission();

        if (permissionState === 'denied') {
          return;
        }

        // Si el permiso está en 'prompt', intentar solicitar permisos
        if (permissionState === 'prompt') {
          const permissionGranted = await this.requestGeolocationPermission();
          if (!permissionGranted) {
            const errorMessage = 'Se requiere permiso de ubicación para continuar.';
            this.store.dispatch(
              NOTIFICATION_SHOW({
                notification: {
                  type: 'warning',
                  title: 'Permiso Requerido',
                  detail: errorMessage,
                },
              })
            );
            reject(errorMessage);
            return;
          }
        }

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const location: IUserLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            // Obtener la dirección usando el geocoder
            try {
              const details = await this.getAddressDetailsFromCoordinates(location);
              location.address = details.address;
              location.country = details.country;
              location.countryCode = details.countryCode;
            } catch (error) {
              this.store.dispatch(
                NOTIFICATION_SHOW({
                  notification: {
                    type: 'error',
                    title: 'Error',
                    detail: 'Error al obtener la dirección: ' + error,
                  },
                })
              );
            }

            resolve(location);
          },
          (error) => {
            let errorMessage = 'Error desconocido al obtener la ubicación';

            switch(error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Usuario denegó la solicitud de geolocalización. Por favor, habilita la ubicación en la configuración de tu navegador.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'La información de ubicación no está disponible';
                break;
              case error.TIMEOUT:
                errorMessage = 'Se agotó el tiempo de espera para obtener la ubicación';
                break;
            }

            this.store.dispatch(
              NOTIFICATION_SHOW({
                notification: {
                  type: 'error',
                  title: 'Error',
                  detail: errorMessage,
                },
              })
            );
            reject(errorMessage);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000, // Aumentado a 10 segundos
            maximumAge: 0
          }
        );
      })
    );
  }

  initAutocomplete(inputElement: HTMLInputElement, countryCode: string = 'us'): void {
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.error('Google Maps Places API o Places library no está cargada. Asegúrate de incluir "&libraries=places" en la URL de carga de la API.');
      setTimeout(() => this.initAutocomplete(inputElement, countryCode), 500);
      return;
    }

    const options: google.maps.places.AutocompleteOptions = {
      componentRestrictions: { country: countryCode }, // Por defecto a 'us'
      types: ['address'], // Sugerir solo direcciones
      fields: ['address_components', 'formatted_address', 'geometry', 'place_id'] // Campos para optimizar costos
    };

    const autocomplete = new google.maps.places.Autocomplete(inputElement, options);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        this._autocompletePlaceSelected.next(place);
      } else {
        console.warn("No se seleccionó una sugerencia de dirección válida.");
        this._autocompletePlaceSelected.next(null!);
      }
    });
  }

  getAddressDetailsFromCoordinates(location: IUserLocation): Promise<{ address: string; country?: string; countryCode?: string }> {
    return new Promise((resolve, reject) => {
      this.geocoder.geocode(
        {
          location: { lat: location.lat, lng: location.lng }
        },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus | string) => {
          if (status === 'OK' && results && results[0]) {
            const countryComp = results[0].address_components?.find((c: google.maps.GeocoderAddressComponent) => c.types?.includes('country'));
            resolve({
              address: results[0].formatted_address,
              country: countryComp?.long_name,
              countryCode: countryComp?.short_name
            });
          } else {
            reject('No se pudo obtener la dirección');
          }
        }
      );
    });
  }

  getAddressFromCoordinates(location: IUserLocation): Promise<string> {
    return this.getAddressDetailsFromCoordinates(location).then(res => res.address);
  }

  getCoordinatesFromAddress(address?: string, countryCode?: string): Observable<IUserLocation> {
    return new Observable(observer => {
      if (!address) {
        observer.error('No se proporcionó una dirección');
        return;
      }

      const request: google.maps.GeocoderRequest = { address };
      if (countryCode) {
        request.componentRestrictions = { country: countryCode };
      }

      this.geocoder.geocode(
        request,
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus | string) => {
          if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
            const countryComp = results[0].address_components?.find((c: google.maps.GeocoderAddressComponent) => c.types?.includes('country'));
            const location: IUserLocation = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
              address: results[0].formatted_address,
              country: countryComp?.long_name,
              countryCode: countryComp?.short_name
            };
            observer.next(location);
            observer.complete();
          } else {
            const errorMessage = `No se pudieron obtener las coordenadas para la dirección proporcionada: "${address}"`;
            observer.error(errorMessage);
          }
        }
      );
    });
  }

  calculateDistance(origin: IUserLocation, destination: IUserLocation): Promise<IDistanceResult> {
    return new Promise((resolve, reject) => {
      try {
        const originLatLng = new google.maps.LatLng(origin.lat, origin.lng);
        const destinationLatLng = new google.maps.LatLng(destination.lat, destination.lng);

        // Calcular distancia usando la geometría esférica de Google Maps
        const distanceInMeters = google.maps.geometry.spherical.computeDistanceBetween(
          originLatLng,
          destinationLatLng
        );

        // Formatear la distancia para mostrar
        let distanceText: string;
        if (distanceInMeters < 1000) {
          distanceText = `${Math.round(distanceInMeters)} metros`;
        } else {
          const distanceInKm = distanceInMeters / 1000;
          distanceText = `${distanceInKm.toFixed(2)} kilómetros`;
        }

        const result: IDistanceResult = {
          distanceInMeters,
          distanceText,
          originLocation: origin,
          destinationLocation: destination
        };

        resolve(result);
      } catch (error) {
        const errorMessage = 'Error al calcular la distancia entre las ubicaciones';
        reject(errorMessage);
      }
    });
  }

  calculateDistance$(origin: IUserLocation, destination: IUserLocation): Observable<IDistanceResult> {
    return from(this.calculateDistance(origin, destination));
  }

  calculateDistanceByAddress(originAddress: string, destinationAddress: string): Observable<IDistanceResult> {
    return this.getCoordinatesFromAddress(originAddress).pipe(
      mergeMap(origin =>
        this.getCoordinatesFromAddress(destinationAddress).pipe(
          mergeMap(destination =>
            from(this.calculateDistance(origin, destination))
          )
        )
      )
    );
  }

  calculateDistanceByAddress$(originAddress: string, destinationAddress: string): Observable<IDistanceResult> {
    return this.calculateDistanceByAddress(originAddress, destinationAddress);
  }

  isPointInShape(point: IUserLocation, zone: DeliveryZone, storeLocation?: IUserLocation | null): boolean {
    if (!zone.puntoMapa || zone.puntoMapa.length === 0) {
      if (storeLocation && storeLocation.lat && storeLocation.lng) {
        const fallbackRadio = zone.valorUnidadMedida || 10;
        let radioEnMetros = fallbackRadio;
        if (zone.tipoUnidadMedida === 'MILLAS') {
          radioEnMetros = fallbackRadio * 1609.34;
        } else if (zone.tipoUnidadMedida === 'KILOMETROS') {
          radioEnMetros = fallbackRadio * 1000;
        }
        
        return this.isPointInCircle(
          point,
          { lat: storeLocation.lat, lng: storeLocation.lng },
          radioEnMetros
        );
      }
      return false;
    }

    const firstPoint = zone.puntoMapa[0];
    const center = {
      lat: parseFloat(firstPoint.lat),
      lng: parseFloat(firstPoint.lng)
    };

    switch (zone.tipoZonaMapa) {
      case 'CIRCULO':
        // Convertir el radio según el tipo de unidad de medida
        let radioEnMetros = parseFloat(firstPoint.radio);
        if (zone.tipoUnidadMedida === 'MILLAS') {
          // 1 milla = 1.60934 kilómetros = 1609.34 metros
          radioEnMetros = radioEnMetros * 1609.34;
        } else if (zone.tipoUnidadMedida === 'KILOMETROS') {
          // 1 kilómetro = 1000 metros
          radioEnMetros = radioEnMetros * 1000;
        }

        return this.isPointInCircle(
          point,
          center,
          radioEnMetros
        );
      case 'POLIGONO':
        if (firstPoint.norte && firstPoint.sur && firstPoint.este && firstPoint.oeste) {
          const inRectangle = this.isPointInRectangle(point, {
            north: parseFloat(firstPoint.norte),
            south: parseFloat(firstPoint.sur),
            east: parseFloat(firstPoint.este),
            west: parseFloat(firstPoint.oeste)
          });
          if (inRectangle) return true;
        }

        const polygonPoints = zone.puntoMapa.map(punto => ({
          lat: parseFloat(punto.lat),
          lng: parseFloat(punto.lng)
        }));
        return this.isPointInPolygon(point, polygonPoints);
      case 'RECTANGULO':
        if (!firstPoint.norte || !firstPoint.sur || !firstPoint.este || !firstPoint.oeste) {
          return false;
        }
        return this.isPointInRectangle(point, {
          north: parseFloat(firstPoint.norte),
          south: parseFloat(firstPoint.sur),
          east: parseFloat(firstPoint.este),
          west: parseFloat(firstPoint.oeste)
        });
      default:
        return false;
    }
  }

  isPointInCircle(point: IUserLocation, center: IUserLocation, radiusInMeters: number): boolean {
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(center.lat, center.lng),
      new google.maps.LatLng(point.lat, point.lng)
    );

    return distance <= radiusInMeters;
  }

  isPointInPolygon(point: IUserLocation, polygonPoints: IUserLocation[]): boolean {
    const polygon = new google.maps.Polygon({
      paths: polygonPoints.map(p => ({ lat: p.lat, lng: p.lng }))
    });
    return google.maps.geometry.poly.containsLocation(
      new google.maps.LatLng(point.lat, point.lng),
      polygon
    );
  }

  isPointInRectangle(point: IUserLocation, bounds: { north: number; south: number; east: number; west: number }): boolean {
    return point.lat <= bounds.north &&
           point.lat >= bounds.south &&
           point.lng <= bounds.east &&
           point.lng >= bounds.west;
  }

  createDeliveryZone(center: IUserLocation, radiusInMeters: number, nombre: string): DeliveryZone {
    return {
      idZonaEntrega: '', // Se debe generar o asignar
      nombre: nombre,
      tipoZonaMapa: 'CIRCULO',
      tipoZonaEntrega: 'DISTANCIA',
      puntoMapa: [{
        radio: radiusInMeters.toString(),
        lat: center.lat.toString(),
        lng: center.lng.toString(),
        norte: '',
        sur: '',
        este: '',
        oeste: ''
      }],
      // Otros campos requeridos con valores por defecto
      tarifa: 0,
      tipoTarifa: 'DOLAR',
      valorCompensacion: 0,
      tipoValorCompensacion: 'DOLAR',
      valorUnidadMedida: radiusInMeters,
      tipoUnidadMedida: 'MILLAS',
      tiempo: '30',
      pagoImpuestoCliente: false,
      color: '#FF0000',
      fechaCreacion: new Date().toISOString(),
      fechaActualizacion: new Date().toISOString(),
      localId: ''
    };
  }

  createCircleShape(center: IUserLocation, radiusInMeters: number): IGeometricShape {
    return {
      tipoZonaMapa: 'CIRCULO',
      radio: radiusInMeters
    };
  }

  createPolygonShape(points: IUserLocation[]): IGeometricShape {
    return {
      tipoZonaMapa: 'POLIGONO',
      paths: points
    };
  }

  createRectangleShape(bounds: { north: number; south: number; east: number; west: number }): IGeometricShape {
    return {
      tipoZonaMapa: 'RECTANGULO',
      bounds: bounds
    };
  }
}
